import { ReactNode, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { MainLayout } from './MainLayout';
import { EmbeddedLayoutChrome } from './EmbeddedLayoutChrome';

export type WorkspaceSection = 'messages' | 'servers' | 'contacts' | 'settings';

type DesktopWorkspaceShellProps = {
  activeSection: WorkspaceSection;
  master: ReactNode;
  detail?: ReactNode;
  emptyTitle: string;
  emptyDescription: string;
};

type NavItem = {
  section: WorkspaceSection;
  path: string;
  label: string;
  icon: 'chat' | 'server' | 'contact' | 'settings';
};

function NavIcon({ icon }: { icon: NavItem['icon'] }) {
  const className = 'h-5 w-5';

  if (icon === 'chat') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.15} d="M8 10h8M8 14h5M21 12a9 9 0 11-4-7.5L21 12z" />
      </svg>
    );
  }

  if (icon === 'server') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="9" cy="15" r="4.8" strokeWidth={2.2} />
        <circle cx="15" cy="9" r="4.8" strokeWidth={2.2} />
        <path strokeLinecap="round" d="M11.7 12.3 L12.3 11.7" strokeWidth={1.9} />
      </svg>
    );
  }

  if (icon === 'contact') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.1} d="M15 8a3 3 0 11-6 0 3 3 0 016 0zM5 19a7 7 0 0114 0" />
      </svg>
    );
  }

  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="6" strokeWidth={2} />
      <circle cx="12" cy="12" r="2.5" strokeWidth={2} />
      <path strokeLinecap="round" strokeWidth={2} d="M12 3.5 L12 5.3M12 18.7 L12 20.5M3.5 12 L5.3 12M18.7 12 L20.5 12" />
      <path strokeLinecap="round" strokeWidth={2} d="M6.2 6.2 L7.5 7.5M16.5 16.5 L17.8 17.8M16.5 7.5 L17.8 6.2M6.2 17.8 L7.5 16.5" />
    </svg>
  );
}

function EmptyDetail({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-full min-h-0 items-center justify-center px-8">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-[color:var(--border-soft)] bg-white/75 text-[color:var(--text-soft)] shadow-[0_14px_34px_rgba(53,38,27,0.06)]">
          <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.9} d="M8 10h8M8 14h5M21 12a9 9 0 11-4-7.5L21 12z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-[color:var(--text-strong)]">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-[color:var(--text-muted)]">{description}</p>
      </div>
    </div>
  );
}

export function DesktopWorkspaceShell({
  activeSection,
  master,
  detail,
  emptyTitle,
  emptyDescription
}: DesktopWorkspaceShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  const items: NavItem[] = useMemo(
    () => [
      { section: 'messages', path: '/messages', label: t('nav.messages'), icon: 'chat' },
      { section: 'servers', path: '/servers', label: t('nav.servers'), icon: 'server' },
      { section: 'contacts', path: '/contacts', label: t('nav.contacts'), icon: 'contact' },
      { section: 'settings', path: '/settings', label: t('nav.settings'), icon: 'settings' }
    ],
    [t]
  );

  return (
    <MainLayout hideNav>
      <div className="grid h-full min-h-0 grid-cols-[4.75rem_minmax(20rem,26rem)_minmax(0,1fr)] overflow-hidden bg-[rgba(255,250,247,0.58)]">
        <aside className="flex min-h-0 flex-col items-center gap-2 border-r border-[color:var(--border-soft)] bg-[rgba(255,252,250,0.78)] px-2 py-3 backdrop-blur-xl">
          <nav className="flex w-full flex-1 flex-col items-center gap-1" aria-label="Navigation">
            {items.map((item) => {
              const active = activeSection === item.section || location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <button
                  key={item.section}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className={`group flex h-[4.25rem] w-full flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-medium transition-colors ${
                    active
                      ? 'bg-[rgba(233,92,63,0.13)] text-[color:var(--text-strong)] shadow-[inset_0_0_0_1px_rgba(232,92,63,0.16)]'
                      : 'text-[color:var(--text-soft)] hover:bg-white/76 hover:text-[color:var(--text-strong)]'
                  }`}
                  aria-current={active ? 'page' : undefined}
                  title={item.label}
                >
                  <NavIcon icon={item.icon} />
                  <span className="max-w-full truncate px-1 leading-none">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <section className="min-h-0 overflow-hidden border-r border-[color:var(--border-soft)] bg-[rgba(255,250,247,0.72)]">
          <EmbeddedLayoutChrome>{master}</EmbeddedLayoutChrome>
        </section>

        <main className="min-h-0 overflow-hidden bg-[rgba(255,252,250,0.54)]">
          <EmbeddedLayoutChrome>
            {detail || <EmptyDetail title={emptyTitle} description={emptyDescription} />}
          </EmbeddedLayoutChrome>
        </main>
      </div>
    </MainLayout>
  );
}
